<template>
  <Dialog
    class="custom-footer"
    :draggable="false"
    v-model:visible="visible"
    :header=" id ? 'Edit product' : 'Add new product'"
    :style="{width: '455px'}"
    :modal="true"
    @hide="close()"
  >

    <div class="p-d-flex p-ai-center p-my-3" v-if="isLoading">
      <ProgressSpinner class="small" />
    </div>

    <form v-else @submit.prevent="submit()">
      <div class="p-fluid">
        <FormField title="Select the product type:" :error="getError('type')" componentType="custom">
          <div class="product-types">
            <Button class="p-button-secondary" v-for="type in types" :key="type.value" :label="type.label" :class="{selected: product.type === type.value}" @click="selectType(type)" />
          </div>

        </FormField>
        <FormField v-model="product.title" title="Product title " :required="true" :error="getError('title')" autofocus placeholder="Add product title" />
        <FormField v-model="product.description" title="Product description" :required="true" :error="getError('description')" placeholder="Add product description..." componentType="textarea" />

        <div>
          <div class="image-title">Product image</div>
          <div class="image-note">
            The image size should be &lt; {{ format(config.maxUploadSize) }}
          </div>
          <div class="image" v-if="image.objectURL">
            <Avatar :image="image.objectURL || photo" class="p-mr-3" size="xlarge" />
            <div class="image-text">
              <div class="image-name">{{ image.name }}</div>
              <div class="image-size">{{ format(image.size) }}</div>
            </div>
          </div>
          <FormField :error="getError('photos.0')" ref="imageComponent" :multiple="false" :fileLimit="1" mode="basic" :customUpload="true" accept="image/*" :maxFileSize="config.maxUploadSize" @select="selectImage" class="p-mr-4" componentType="upload" chooseLabel="Upload photo" />
        </div>
        <FormField v-model="product.link_to_website" title="Product website" :error="getError('link_to_website')" placeholder="Link on website" />
        <FormField v-model="product.value" title="Product retail price (USD)" :error="getError('value')" placeholder="$10.00" class="p-mb-0" />

        <div class="p-hidden">
          <Button type="submit" label="Add Product" :loading="busy" class="full-width"></Button>
        </div>

      </div>
    </form>
    <template #footer>
      <div class="component-footer">
        <Button type="submit" :label="id ? 'Update Product' : 'Add Product'" :disabled="isLoading" :loading="busy" class="full-width" @click.prevent="submit" />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import FormField from '../FormField';
import {computed, defineEmits, defineProps, reactive, ref, toRef, watch} from "vue";
import {useForm} from "../../hooks/useForm";
import {format} from "../../services/Filesize";
import {useProduct} from "../../hooks/useProduct";
import config from '../../config';
import {getProduct, updateProduct} from "../../queries/productQueries";
import {useVModel} from "@vueuse/core";
import ButtonGroup from '../ButtonGroup';

const emit = defineEmits(['update:visible', 'submit', 'close']);
const props = defineProps({
  visible: {type: Boolean, default: false, required: true},
  id: {type: Number},
});

const visible = useVModel(props, 'visible', emit);
const id = toRef(props, 'id');

const {data, isLoading} = getProduct(id, {enabled: computed(() => id.value > 0)});
const {getProducts, create, getTypes} = useProduct();
const {mutateAsync} = updateProduct();

const types = ref(getTypes());
const defaultProductData = {
  title: '',
  description: '',
  link_to_website: '',
  value: '',
  photos: [],
  type: types.value[0].value
};
const image = ref({});
const imageComponent = ref();
const product = reactive({...defaultProductData});

const uploads = ref({
  photo: undefined,
});

watch(() => [props.visible, data.value], (newValue) => {
  refreshProductData();
});

const refreshProductData = () => {
  delete product.delete_photos;
  Object.assign(product, {...(data.value?.data || defaultProductData)});

  if (product.photos.length) {
    image.value = {
      objectURL: product.photos[0].thumbnail,
      name: product.photos[0].name,
      size: product.photos[0].size,
    }
  } else {
    image.value = {};
  }
}

const {busy, submit, getError, reset} = useForm(async () => {
  const data = {...product};

  if (image.value instanceof File) {
    data.photos = [image.value];
  }

  if (data.photos.length) {
    data.photos = data.photos.filter(photo => photo instanceof File);
  }

  const response = id.value ? (await mutateAsync({id: id.value, data})).data : (await create(data)).data;

  emit('submit', response);
});

const selectImage = (event) => {
  if (event.files.length) {
    image.value = event.files[0];
    imageComponent.value.component.clear();

    if (product.photos.length) {
      product.delete_photos = product.photos.map(({ id }) => id);
    }
  }
}

const close = () => {
  visible.value = false;
  emit('close');
}

const selectType = ({ value }) => product.type = value

</script>

<style lang="scss" scoped>
@import "../../assets/sass/theme_variables";

.image-title {
  margin-bottom: toRem(8);
}
.image-note {
  color: $textSecondaryColor;
  margin-bottom: toRem(16);
  font-size: toRem(14);
}

.image {
  display: flex;
  margin-bottom: toRem(16);
}

.image-text {
  display: flex;
  flex-direction: column;
  margin-right: toRem(12);
  justify-content: center;
}

.image-name {
  font-weight: bold;
}

.image-size {
  margin-top: toRem(5);
  color: $textThirdColor;
  font-size: toRem(14);
}


::v-deep(.p-avatar) {
  img {
    border-radius: $borderRadius;
  }
}
::v-deep(.p-field) {
  textarea {
    max-width: 100%;
    min-width: 100%;
    max-height: toRem(200);
    min-height: toRem(100);
  }
}

.product-types {
  display: flex;
  gap: toRem(9);

  .p-button {
    padding-top: 18px;
    padding-bottom: 18px;
    border-width: 2px;
    color: $textSecondaryColor;

    &.selected {
      font-weight: 600;
      border-color: $primaryColor;
      color: $primaryColor;
    }
  }
}

</style>
